import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, BACKEND_URL } from '../config/api';
import { useAuth } from '../context/AuthContext';

const playDing = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.08);
    osc.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.2);
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.5);
    setTimeout(() => ctx.close(), 600);
  } catch {}
};

const Navbar = () => {
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [outbidCount, setOutbidCount] = useState(0);
  const [pendingUsers, setPendingUsers] = useState(0);

  useEffect(() => {
    if (!user?.is_admin) return;
    const load = () => axios.get(`${API}/admin/pending-users-count`).then(r => setPendingUsers(r.data.total)).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    window.addEventListener('pending-users-changed', load);
    return () => { clearInterval(id); window.removeEventListener('pending-users-changed', load); };
  }, [user?.is_admin]);

  const pendingBadge = pendingUsers > 0 ? (
    <span className="ml-1.5 min-w-[18px] h-[18px] inline-flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1" title="Clienti da verificare">{pendingUsers}</span>
  ) : null;
  const [soundOn, setSoundOn] = useState(() => {
    const stored = localStorage.getItem('equus_sound');
    return stored !== 'off';
  });
  const navigate = useNavigate();
  const wsRef = useRef(null);
  const pollRef = useRef(null);
  const soundOnRef = useRef(soundOn);
  const prevCountRef = useRef(0);
  const userIdRef = useRef(null);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const toggleSound = useCallback(() => {
    setSoundOn(prev => {
      const next = !prev;
      localStorage.setItem('equus_sound', next ? 'on' : 'off');
      soundOnRef.current = next;
      return next;
    });
  }, []);

  // Stable fetch function
  const fetchCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/my-bids/outbid-count`);
      const newCount = res.data.outbid_count || 0;
      setOutbidCount(prev => {
        if (prev === 0 && newCount > 0 && soundOnRef.current) {
          playDing();
        }
        prevCountRef.current = newCount;
        return newCount;
      });
    } catch {}
  }, []);

  // WebSocket + polling — only depends on user
  useEffect(() => {
    if (!user) {
      setOutbidCount(0);
      prevCountRef.current = 0;
      userIdRef.current = null;
      return;
    }

    // Skip if same user (avoid re-connect on re-render)
    if (userIdRef.current === user.user_id && wsRef.current) return;
    userIdRef.current = user.user_id;

    fetchCount();
    pollRef.current = setInterval(fetchCount, 30000);

    const connectWs = () => {
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = BACKEND_URL.replace(/^https?:\/\//, '');
      const ws = new WebSocket(`${wsProtocol}//${wsHost}/api/ws/notifications`);
      wsRef.current = ws;

      ws.onopen = () => {
        // Keep alive
        const ping = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send('ping');
          else clearInterval(ping);
        }, 30000);
        ws._pingInterval = ping;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'auction_ended') {
            window.dispatchEvent(new CustomEvent('auction-winner', { detail: data }));
          }
          if (data.type !== 'pong') {
            fetchCount();
          }
        } catch {}
      };

      ws.onclose = () => {
        if (ws._pingInterval) clearInterval(ws._pingInterval);
        wsRef.current = null;
        // Reconnect after 3 seconds if user still logged in
        if (userIdRef.current) {
          setTimeout(() => {
            if (userIdRef.current) connectWs();
          }, 3000);
        }
      };
    };

    connectWs();

    return () => {
      userIdRef.current = null;
      clearInterval(pollRef.current);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        if (wsRef.current._pingInterval) clearInterval(wsRef.current._pingInterval);
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [user?.user_id, fetchCount]);

  const badge = outbidCount > 0 ? (
    <span className="absolute -top-1.5 -right-2.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1 animate-pulse" data-testid="outbid-badge">
      {outbidCount}
    </span>
  ) : null;

  const SoundToggle = ({ className = '' }) => (
    <button
      onClick={toggleSound}
      className={`text-[#64748b] hover:text-[#0a1628] transition-colors ${className}`}
      title={soundOn ? 'Disattiva suono notifiche' : 'Attiva suono notifiche'}
      data-testid="sound-toggle"
    >
      {soundOn ? (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clipRule="evenodd"/>
        </svg>
      ) : (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z" clipRule="evenodd"/>
        </svg>
      )}
    </button>
  );

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="container-custom">
        <div className="flex items-center justify-between h-16">
          <a href="/" className="flex items-center">
            <span className="font-display text-2xl md:text-3xl font-bold text-[#c9a227] tracking-wide">
              EQUUS PRIME
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <a href="/" className="text-[#0a1628] hover:text-[#c9a227] transition-colors font-medium" data-testid="nav-home">Home</a>
            <div className="relative group" data-testid="nav-aste-dropdown">
              <a href="/aste" className="text-[#0a1628] hover:text-[#c9a227] transition-colors font-medium flex items-center gap-1" data-testid="nav-auctions">
                Aste
                <svg className="w-3.5 h-3.5 mt-0.5 opacity-50 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
              </a>
              <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="bg-white border border-gray-200 rounded-lg shadow-lg py-2 min-w-[180px]">
                  <a href="/aste" className="block px-4 py-2 text-sm text-[#0a1628] hover:bg-gray-50 hover:text-[#c9a227] transition-colors font-medium" data-testid="nav-aste-attive">
                    Aste in Corso
                  </a>
                  <a href="/aste?stato=completate" className="block px-4 py-2 text-sm text-[#0a1628] hover:bg-gray-50 hover:text-[#c9a227] transition-colors font-medium" data-testid="nav-aste-completate">
                    Aste Completate
                  </a>
                </div>
              </div>
            </div>
            <a href="/attualita" className="text-[#0a1628] hover:text-[#c9a227] transition-colors font-medium" data-testid="nav-attualita">Attualit&agrave;</a>
            <a href="/risultati" className="text-[#0a1628] hover:text-[#c9a227] transition-colors font-medium" data-testid="nav-results">Risultati</a>
            <a href="/contatti" className="text-[#0a1628] hover:text-[#c9a227] transition-colors font-medium" data-testid="nav-contact">Contatti</a>
            {user?.is_admin && (
              <a href="/admin" className="text-[#c9a227] hover:text-[#c9a227]/80 transition-colors font-medium" data-testid="nav-admin">Admin{pendingBadge && <span data-testid="nav-admin-pending-badge">{pendingBadge}</span>}</a>
            )}
          </div>

          {/* Auth Buttons */}
          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <div className="flex items-center gap-3">
                <a href="/mie-offerte" className="relative text-[#0a1628] hover:text-[#c9a227] transition-colors font-medium" data-testid="nav-my-bids">
                  Le mie offerte
                  {badge}
                </a>
                <SoundToggle />
                <a href="/profilo" className="text-[#0a1628] hover:text-[#c9a227] transition-colors font-medium" data-testid="nav-profile">
                  {user.name}
                </a>
                {!user.is_verified && (
                  <span className="text-xs text-red-500">(Non verificato)</span>
                )}
                <button
                  onClick={handleLogout}
                  className="px-4 py-2 border border-[#0a1628] text-[#0a1628] hover:bg-[#0a1628] hover:text-white transition-colors font-medium"
                  data-testid="logout-btn"
                >
                  Esci
                </button>
              </div>
            ) : (
              <>
                <a href="/login" className="text-[#0a1628] hover:text-[#c9a227] transition-colors font-medium" data-testid="login-link">Accedi</a>
                <a href="/registrati" className="btn-primary" data-testid="register-link">Registrati</a>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-3">
            {user && outbidCount > 0 && (
              <a href="/mie-offerte" className="relative" data-testid="mobile-outbid-icon">
                <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20"><path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z"/></svg>
                <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] flex items-center justify-center bg-red-500 text-white text-[8px] font-bold rounded-full px-0.5">
                  {outbidCount}
                </span>
              </a>
            )}
            <button
              className="text-[#0a1628]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200 py-4">
          <div className="container-custom flex flex-col gap-4">
            <a href="/" className="text-[#0a1628] py-2 font-medium">Home</a>
            <a href="/aste" className="text-[#0a1628] py-2 font-medium">Aste</a>
            <a href="/aste?stato=completate" className="text-[#0a1628] py-2 font-medium pl-4 text-sm">Aste Completate</a>
            <a href="/attualita" className="text-[#0a1628] py-2 font-medium">Attualit&agrave;</a>
            <a href="/risultati" className="text-[#0a1628] py-2 font-medium">Risultati</a>
            <a href="/contatti" className="text-[#0a1628] py-2 font-medium">Contatti</a>
            <hr className="border-gray-200" />
            {user ? (
              <>
                <a href="/profilo" className="text-[#0a1628] py-2 font-medium">{user.name}</a>
                <a href="/mie-offerte" className="flex items-center justify-between py-2 font-medium" data-testid="nav-my-bids-mobile">
                  <span className="text-[#0a1628]">Le mie offerte</span>
                  {outbidCount > 0 && (
                    <span className="min-w-[22px] h-[22px] flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full px-1.5" data-testid="outbid-badge-mobile">
                      {outbidCount}
                    </span>
                  )}
                </a>
                <div className="flex items-center justify-between py-2">
                  <span className="text-[#64748b] text-sm">Suono notifiche</span>
                  <button
                    onClick={toggleSound}
                    className={`relative w-10 h-5 rounded-full transition-colors ${soundOn ? 'bg-[#0a7e8c]' : 'bg-gray-300'}`}
                    data-testid="sound-toggle-mobile"
                  >
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform shadow ${soundOn ? 'translate-x-5' : 'translate-x-0'}`} />
                  </button>
                </div>
                {user.is_admin && <a href="/admin" className="text-[#c9a227] py-2 font-medium" data-testid="mobile-nav-admin">Admin{pendingBadge && <span data-testid="mobile-nav-admin-pending-badge">{pendingBadge}</span>}</a>}
                <button onClick={handleLogout} className="px-4 py-2 border border-[#0a1628] text-[#0a1628] mt-2 font-medium">
                  Esci
                </button>
              </>
            ) : (
              <>
                <a href="/login" className="text-[#0a1628] py-2 font-medium">Accedi</a>
                <a href="/registrati" className="btn-primary text-center mt-2">Registrati</a>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
